import { createFileRoute } from "@tanstack/react-router";
import SeoLandingPage, {
  buildFaqSchema,
  buildLocalBusinessSchema,
} from "@/components/SeoLandingPage";
import heroImage from "@/assets/sprinter-exec-amber.jpg?format=webp&quality=72&w=1400&imagetools";
import midImage from "@/assets/limo-style-sprinter-party-bus-lounge-bar.jpg?format=webp&quality=72&w=1400&imagetools";

const PAGE_URL = "https://atlantaluxurysprinters.com/car-service-pdk-airport";
const PAGE_TITLE = "PDK Car Service | Chauffeur at Every PDK FBO, 24/7";
const PAGE_DESC =
  "PDK car service at Signature, Atlantic Aviation, Million Air and Epps Aviation. We track your tail number, so your chauffeur is staged when you land. Text (678) 431-7222.";

const faqs = [
  {
    q: "Which PDK FBOs do you service?",
    a: "Our chauffeurs cover all PDK FBOs daily, Atlantic Aviation, Million Air, Signature Flight Support, and Epps Aviation. We know each facility's ramp procedures, security protocols, and concierge teams.",
  },
  {
    q: "Can you meet our jet on the PDK ramp?",
    a: "Yes. Where the FBO authorizes ramp access, your Mercedes-Benz Sprinter or Suburban is staged planeside at PDK so passengers transition directly from the aircraft to the vehicle.",
  },
  {
    q: "What vehicles are available for PDK car service?",
    a: "Mercedes-Benz Sprinter Limousines in Jet, Executive, and Limo configurations, plus Suburban SUVs for principals and smaller parties, all chauffeur-driven.",
  },
  {
    q: "Do you provide PDK transportation 24/7?",
    a: "Yes. PDK handles private aviation around the clock and our chauffeur team is available 24/7/365 to match.",
  },
  {
    q: "Where exactly does the chauffeur meet us at PDK?",
    a: "It depends on the FBO. At Atlantic Aviation and Signature Flight Support your chauffeur stages in the FBO's front lot and meets you in the lobby, or moves to the ramp when the line crew authorizes it. At Million Air and Epps Aviation we typically stage curbside at the main entrance off Clairmont Road. Tell us your FBO when you book and we confirm the exact meeting point in your reservation.",
  },
  {
    q: "How early does the chauffeur arrive for a PDK departure?",
    a: "For departures we stage at your pickup address 15 minutes before your requested time, and we build the drive around your FBO's recommended arrival window, usually 20 to 30 minutes before wheels-up for domestic legs.",
  },
  {
    q: "Can you handle luggage, golf clubs, or oversized gear at PDK?",
    a: "Yes. A Mercedes-Benz Sprinter Limousine handles full luggage sets, golf bags, ski gear, and equipment cases that will not fit in a sedan. If you are moving unusual cargo, tell us the piece count and we send the right configuration.",
  },
  {
    q: "What happens if our arrival into PDK is delayed?",
    a: "We track your tail number, so a slipped ETD or a diversion updates your chauffeur automatically. There is no need to call ahead, the vehicle simply arrives against the revised arrival time.",
  },
  {
    q: "Do you coordinate multi-leg itineraries around PDK?",
    a: "Yes. Same-day meeting circuits, hourly-as-directed retainers, and PDK-to-ATL repositioning are all routine. Multi-vehicle groups travel together so everyone arrives at the same time.",
  },
];

export const Route = createFileRoute("/car-service-pdk-airport")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      {
        name: "keywords",
        content:
          "car service PDK airport, PDK airport car service, DeKalb-Peachtree airport chauffeur, PDK FBO transportation, Atlantic Aviation PDK car service, Million Air PDK chauffeur, Signature Flight PDK car service",
      },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: heroImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
      { name: "twitter:image", content: heroImage },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildLocalBusinessSchema({
            url: PAGE_URL,
            name: "Atlanta Luxury Sprinters, Car Service PDK Airport",
            image: heroImage,
            description: PAGE_DESC,
            serviceType: "PDK Airport Chauffeur Service",
            areaServed: [
              { type: "Airport", name: "DeKalb-Peachtree Airport (PDK)" },
              { type: "City", name: "Atlanta" },
              { type: "City", name: "Brookhaven" },
              { type: "City", name: "Chamblee" },
            ],
          })
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(buildFaqSchema(faqs)),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      eyebrow="PDK • DeKalb-Peachtree Airport"
      h1="Car Service at PDK Airport"
      intro="Your aircraft plans can change. We track your tail number and coordinate your PDK pickup at Atlantic Aviation, Million Air, Signature or Epps Aviation. Choose a Suburban SUV or Mercedes-Benz Sprinter Limousine."
      heroImage={heroImage}
      heroAlt="Passengers descending airstairs from a private jet at DeKalb-Peachtree Airport (PDK) FBO"
      midImage={midImage}
      midAlt="Private jet inside a luxury FBO hangar at DeKalb-Peachtree Airport (PDK) Atlanta"
      sectionOneTitle="Your PDK Pickup, Personally Arranged"
      sectionOneBody={[
        "PDK is Georgia's second-busiest airport and metro Atlanta's primary private aviation hub. My chauffeurs serve all four FBOs daily: Atlantic Aviation, Million Air, Signature Flight Support and Epps Aviation.",
        "We coordinate with the line crew and concierge desk. Your Suburban or Sprinter meets you at the agreed location, with planeside pickup where the FBO authorizes access.",
      ]}
      bulletsTitle="PDK Car Service Done Right"
      bulletsIntro="Tail-number tracking and a meeting point confirmed with your FBO."
      bullets={[
        "All four PDK FBOs covered daily",
        "Live tail-number tracking",
        "Planeside ramp pickup where authorized",
        "Mercedes-Benz Sprinter Limo, Executive, and Jet styles",
        "Suburban SUVs for principals, families and smaller parties, up to 6 or 7",
        "Direct flight department coordination",
      ]}
      coverageTitle="Common PDK Destinations"
      coverageIntro="From PDK to anywhere in metro Atlanta, typical transfer times shown are off-peak."
      coverage={[
        "Buckhead, 15 min",
        "Midtown Atlanta, 25 min",
        "Downtown Atlanta, 30 min",
        "Sandy Springs, 15 min",
        "Dunwoody, 12 min",
        "Alpharetta, 25 min",
        "Mercedes-Benz Stadium, 30 min",
        "ATL Airport, 35 min",
        "Athens, GA, 70 min",
      ]}
      routeTable={{
        title: "PDK Drive Times and Route Detail",
        intro:
          "PDK sits on Clairmont Road in Chamblee, minutes from I-85 and GA-400. These are the transfers we run most often, with the roads your chauffeur actually uses.",
        rows: [
          {
            route: "PDK → Buckhead",
            distance: "~9 miles",
            time: "15–25 min",
            notes: "Clairmont Rd to I-85 south, exit at Lenox or Piedmont. Slowest 4–6pm weekdays.",
          },
          {
            route: "PDK → Midtown Atlanta",
            distance: "~13 miles",
            time: "22–35 min",
            notes: "I-85 south to the 10th/14th Street exits, plan extra time on event nights.",
          },
          {
            route: "PDK → Downtown Atlanta",
            distance: "~16 miles",
            time: "28–45 min",
            notes: "I-85 south to the Downtown Connector. Add 20 min around stadium events.",
          },
          {
            route: "PDK → Sandy Springs / Perimeter",
            distance: "~9 miles",
            time: "15–20 min",
            notes: "I-285 west, exit Ashford-Dunwoody or Peachtree Dunwoody.",
          },
          {
            route: "PDK → Dunwoody",
            distance: "~7 miles",
            time: "12–18 min",
            notes: "Shortest common transfer. Surface roads via Chamblee-Dunwoody.",
          },
          {
            route: "PDK → Alpharetta / Avalon",
            distance: "~22 miles",
            time: "25–45 min",
            notes: "I-285 west to GA-400 north. Northbound 400 backs up after 3:30pm.",
          },
          {
            route: "PDK → Mercedes-Benz Stadium",
            distance: "~18 miles",
            time: "30–50 min",
            notes: "Game days require a staged pickup point, we confirm it the morning of.",
          },
          {
            route: "PDK → Hartsfield-Jackson (ATL)",
            distance: "~26 miles",
            time: "35–55 min",
            notes: "Commercial connections after a private leg. We build in a buffer for check-in.",
          },
          {
            route: "PDK → Athens, GA",
            distance: "~65 miles",
            time: "70–90 min",
            notes: "I-85 north to GA-316. Popular for UGA gamedays and campus visits.",
          },
        ],
        footnote:
          "Drive times are typical door-to-door ranges including FBO staging. Your chauffeur monitors traffic live and reroutes as needed.",
      }}
      detailSections={[
        {
          title: "FBO-by-FBO Pickup Instructions at PDK",
          body: [
            "PDK has four fixed-base operators, and each one handles ground transportation slightly differently. Knowing the difference is what separates a smooth arrival from a phone call on the ramp.",
          ],
          list: [
            "Atlantic Aviation (1951 Airport Rd), chauffeur checks in at the front desk and stages in the FBO lot; ramp access granted case by case by the line crew.",
            "Signature Flight Support (1954 Airport Rd), lobby meet is standard; planeside pickup available when your flight crew requests it in advance.",
            "Million Air (1000 Aviation Blvd), curbside staging at the main entrance; the concierge desk pages your chauffeur as you deplane.",
            "Epps Aviation (1 Aviation Way), chauffeur waits in the executive lounge; Epps typically permits ramp-side loading for larger parties.",
          ],
        },
        {
          title: "How a PDK Transfer Actually Runs",
          body: [
            "When you book, we take four things: tail number, FBO, passenger and bag count, and destination. The tail number is the important one, it lets us watch your actual flight rather than a scheduled time, so a 40-minute early arrival or a weather hold both resolve themselves without a phone call.",
            "Your chauffeur is on airport property before your aircraft is on final. For departures out of PDK, we work backward from your FBO's recommended arrival window and stage at your address 15 minutes early, so nobody is waiting on a vehicle.",
            "Groups moving in more than one vehicle travel as a convoy with a single lead chauffeur, which keeps a board or an entourage arriving together instead of trickling in.",
          ],
        },
        {
          title: "Which Vehicle Fits Your PDK Party",
          list: [
            "1–3 passengers with light bags, Suburban SUV, quickest curbside turn at any PDK FBO.",
            "4–8 passengers, executive travel, Sprinter Executive with facing captain's chairs and worktable.",
            "8–14 passengers, groups and entourages, Mercedes-Benz Sprinter Limousine, full luggage capacity.",
            "Full luggage sets, golf bags, or equipment cases, Sprinter configuration with rear cargo retained.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        {
          to: "/private-jet-car-service-atlanta",
          title: "Private Jet Car Service Atlanta",
          desc: "Our flagship private aviation chauffeur service.",
        },
        {
          to: "/fbo-transportation-atlanta",
          title: "FBO Transportation Atlanta",
          desc: "Coverage at every major Atlanta FBO.",
        },
        {
          to: "/chauffeur-service-buckhead-from-airport",
          title: "Buckhead Chauffeur From Airport",
          desc: "Direct PDK-to-Buckhead executive transfers.",
        },
      ]}
      ctaTitle="Text Me Your PDK Arrival"
      ctaBody="Send your tail number, FBO, arrival time, passengers and bags. I'll check availability myself and send your transfer quote in writing."
    />
  );
}
